import { inject, provideAppInitializer } from '@angular/core';
import { AclRoles, AclService } from '@h5s/util-acl';
import { AuthService } from '@h5s/util-auth';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../environments/environment';

/**
 * Inizializzazione dell'autenticazione allo startup dell'applicazione
 */
function initializeAuth(
  oauthService: OAuthService,
  authService: AuthService,
  aclService: AclService,
): () => Promise<void> {
  return async () => {
    oauthService.configure(environment.authConfig);

    // Va letto PRIMA di tryLogin, che rimuove code/state dall'URL con un history.replaceState.
    const isAuthorizationCallback = authService.hasAuthorizationCodeInUrl();

    // Carica il discovery document (necessario per refreshToken) e completa il login
    // se stiamo tornando dall'Identity Provider con un authorization code.
    await oauthService.loadDiscoveryDocumentAndTryLogin();

    // Per garantire che i claim siano presenti, è necessario controllarli dopo aver caricato il discovery del document
    initializeAcl(authService, aclService);

    // Solo su un vero callback: salva la rotta trasportata dallo `state`, oppure ripulisce
    // un valore stantio. Il gate è necessario perché uno `state` utente vuoto non è
    // distinguibile da "nessun login": senza di esso un deep link abbandonato potrebbe
    // dirottare un caricamento successivo.
    if (isAuthorizationCallback) {
      authService.storePendingRedirectFromLoginState();
    }

    // Token scaduto ma refresh token ancora presente: tentiamo un refresh silenzioso
    // prima di forzare un redirect completo alla login (evita flash e toast di errore).
    if (!oauthService.hasValidAccessToken() && oauthService.getRefreshToken()) {
      try {
        await oauthService.refreshToken();
      } catch {
        // Refresh fallito (es. refresh token scaduto): si prosegue con initCodeFlow qui sotto.
      }
    }

    if (!oauthService.hasValidAccessToken()) {
      // Il redirect URI è fisso per bundle: la rotta richiesta viaggia nel parametro `state`
      // e viene ripristinata dopo il bootstrap.
      authService.initCodeFlowPreservingRoute();

      // Evita il bootstrap dell'applicazione se non sei loggato
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise<void>(() => {});
    }

    // Sessione valida stabilita: attiviamo il refresh automatico in background.
    // Con code flow + offline_access usa il grant refresh_token, non l'iframe.
    oauthService.setupAutomaticSilentRefresh();
  };
}

function initializeAcl(authService: AuthService, aclService: AclService): void {
  const roles = authService.isAdminUser ? [AclRoles.Admin] : [AclRoles.Basic];
  aclService.setRoles(roles);
}

export const authInitializerProvider = provideAppInitializer(() => {
  const initializerFn = initializeAuth(inject(OAuthService), inject(AuthService), inject(AclService));
  return initializerFn();
});
